import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { listReports } from "@/lib/api";
import { FileBarChart, ShieldCheck, Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_ICONS = {
  Security: ShieldCheck,
};

export default function AdminReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await listReports();
        setReports(data.reports || []);
      } catch (e) {
        const msg = e?.response?.data?.detail || "Failed to load reports";
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <AdminLayout title="Reports" breadcrumb="Reports">
      <div className="max-w-5xl" data-testid="admin-reports-page">
        <div className="mb-6 flex items-start gap-3">
          <div className="bg-teal/10 p-2 rounded-md">
            <FileBarChart className="w-6 h-6 text-teal" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-xl text-navy">Available Reports</h2>
            <p className="text-slate-600 text-sm">
              Select a report to view details. Apply filters such as date range to narrow results.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-teal" />
          </div>
        ) : reports.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-slate-500">
              No reports available yet.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reports.map((r) => {
              const Icon = CATEGORY_ICONS[r.category] || FileBarChart;
              return (
                <Link
                  key={r.key}
                  to={`/admin/reports/${r.key}`}
                  data-testid={`report-card-${r.key}`}
                  className="block"
                >
                  <Card className="hover:shadow-md transition-shadow border-slate-200 hover:border-teal group">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center justify-between text-base">
                        <span className="flex items-center gap-2">
                          <Icon className="w-5 h-5 text-teal" />
                          {r.name}
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-teal transition-colors" />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-600">{r.description}</p>
                      <span className="inline-block mt-3 text-xs font-medium uppercase tracking-wide text-slate-500">
                        {r.category}
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
